// ===== Room List (Sidebar) =====
// CAP04: 사이드바 대화 목록의 데이터 소스를 T1 room substrate(GET /api/rooms)로 교체한다.
// "라벨만 룸으로 바꾼 세션 나열" 위장을 막기 위해 렌더 데이터는 반드시 /api/rooms 응답이어야 한다.
// 룸 클릭은 기존 세션 클릭 경로를 재사용한다: wsClient.resumeSession + router.navigate("chat").

const RoomList = (() => {
  interface CoreRoom {
    id: string;
    roomType: string;
    title?: string | null;
    sessionId?: string | null;
    lastMessagePreview?: string | null;
    isDefault?: boolean;
    // 그 방(세션)에서 내가 안 읽은 메시지 수 — GET /api/rooms가 실어 준다(읽음 기록 미가용이면 부재).
    unreadCount?: number;
  }

  type RoomNavigationTarget =
    | { status: "room"; room: CoreRoom }
    | { status: "session"; sessionId: string }
    | { status: "none" }
    | { status: "invalid"; reason: string };

  interface CoreRoomMember {
    actorId: string;
    displayName: string;
  }

  const ROOM_TYPE_LABELS: Record<string, string> = { team: "팀", dm: "1:1", group: "그룹" };
  // 룸 아바타 폴백 색은 design-tokens.css --peer-avatar-* (슬레이트 6톤)이 소유. 룸 id 기준 결정적 배정.
  const PEER_AVATAR_COUNT = 6;

  let roomSessionIds = new Set<string>();
  let roomIdBySession = new Map<string, string>();
  let roomBySession = new Map<string, CoreRoom>();
  let roomById = new Map<string, CoreRoom>();
  let defaultRoom: CoreRoom | null = null;
  // Phase 3(bigtionax-agent-room-conversation-picker): 마지막 /api/rooms refresh 성공 여부.
  // 미로딩 상태에서 getRoomIdForSession이 항상 null을 반환하는 fail-open과, "룸이 아님을 확인함"을
  // 구분하기 위한 공개 신호(예: startNewConversation의 비룸 양성 확인 가드).
  let _loaded = false;
  // custom title이 없는 dm/group 룸의 members 조합 라벨 캐시 (roomId -> label). refresh()마다 재구성.
  let roomMemberLabels = new Map<string, string>();
  // 사이드바 방별 안 읽음 숫자 (sessionId -> count). /api/rooms 응답으로 채우고, WS
  // message-unread-total 신호를 받으면 refreshUnreadCounts가 /api/rooms/unread-count의
  // sessionUnreadCounts 스냅샷으로 통째 교체한다(부재 = 0).
  let unreadBySession = new Map<string, number>();
  const UNREAD_REFRESH_DEBOUNCE_MS = 250;
  let _unreadRefreshTimer: ReturnType<typeof setTimeout> | null = null;
  let _unreadRefreshSeq = 0;
  // 행 버튼 -> 그 행의 pill. 칠할 때 요소 질의(querySelector*)를 새로 쓰지 않으려고 렌더 시 기억한다.
  const unreadPillByButton = new WeakMap<HTMLElement, HTMLElement>();

  function _isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null;
  }

  function _isRoom(value: unknown): value is CoreRoom {
    return _isRecord(value)
      && typeof value.id === "string"
      && typeof value.roomType === "string"
      && (!("title" in value) || value.title == null || typeof value.title === "string")
      && (!("sessionId" in value) || value.sessionId == null || typeof value.sessionId === "string")
      && (!("lastMessagePreview" in value) || value.lastMessagePreview == null || typeof value.lastMessagePreview === "string")
      && (!("isDefault" in value) || typeof value.isDefault === "boolean")
      && (!("unreadCount" in value) || value.unreadCount == null || typeof value.unreadCount === "number");
  }

  function _isRoomMember(value: unknown): value is CoreRoomMember {
    return _isRecord(value) && typeof value.actorId === "string" && typeof value.displayName === "string";
  }

  function _typeLabel(roomType: string): string {
    return ROOM_TYPE_LABELS[roomType] || roomType;
  }

  function _roomTitle(room: CoreRoom): string {
    if (room.title) return room.title;
    const memberLabel = roomMemberLabels.get(room.id);
    if (memberLabel) return memberLabel;
    return room.roomType === "team" ? "팀 룸" : "1:1 대화";
  }

  // members(GET /api/rooms/:id/members의 displayName)에서 self를 제외한 상대 라벨 조합.
  // 1명이면 이름 그대로, 2명이면 "A, B", 3명 이상이면 "A, B 외 N".
  function _roomMemberLabel(members: CoreRoomMember[], selfActorId: string | null): string | null {
    const others = members.filter((member) => member.actorId !== selfActorId);
    if (others.length === 0) return null;
    if (others.length <= 2) return others.map((m) => m.displayName).join(", ");
    return `${others[0].displayName}, ${others[1].displayName} 외 ${others.length - 2}`;
  }

  async function _fetchSelfActorId(): Promise<string | null> {
    try {
      const res = await fetch("/api/rooms/actors");
      if (!res.ok) return null;
      const data: unknown = await res.json();
      const users = _isRecord(data) && Array.isArray(data.users) ? data.users : [];
      const self = users.find((user) => _isRecord(user) && user.isSelf === true && typeof user.actorId === "string");
      return _isRecord(self) && typeof self.actorId === "string" ? self.actorId : null;
    } catch (err) {
      return null;
    }
  }

  // custom title이 없는 team 이외 룸만 members를 조회해 라벨 캐시를 채운다.
  async function _buildMemberLabels(rooms: CoreRoom[]): Promise<void> {
    const targets = rooms.filter((room) => !room.title && room.roomType !== "team");
    roomMemberLabels = new Map<string, string>();
    if (targets.length === 0) return;

    const selfActorId = await _fetchSelfActorId();

    await Promise.all(targets.map(async (room) => {
      try {
        const res = await fetch(`/api/rooms/${room.id}/members`);
        if (!res.ok) return;
        const data: unknown = await res.json();
        const members = _isRecord(data) && Array.isArray(data.members)
          ? data.members.filter(_isRoomMember)
          : [];
        const label = _roomMemberLabel(members, selfActorId);
        if (label) roomMemberLabels.set(room.id, label);
      } catch (err) {
        // members 조회 실패 시 _roomTitle의 타입명 폴백을 그대로 사용
      }
    }));
  }

  function _initial(text: string): string {
    const trimmed = (text || "").trim();
    return trimmed ? (trimmed[0] ?? "#").toUpperCase() : "#";
  }

  function _slateColor(seed: string | null | undefined): string {
    const s = String(seed || "");
    let h = 0;
    for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
    return `var(--peer-avatar-${h % PEER_AVATAR_COUNT})`;
  }

  function _setActive(btn: HTMLButtonElement): void {
    const list = document.getElementById("roomList");
    if (!list) return;
    list.querySelectorAll(".room-item.active").forEach((el) => el.classList.remove("active"));
    btn.classList.add("active");
    // 지금 열어 둔 방은 숫자를 숨긴다(보이는 메시지는 곧 읽음 처리된다) — active가 옮겨 가면
    // 떠난 방의 남은 숫자가 다시 보이도록 전체를 다시 칠한다.
    _paintAllUnread();
  }

  function _unreadLabel(count: number): string {
    return count > 99 ? "99+" : String(count);
  }

  // 방 행 하나의 안 읽음 표시를 현재 상태로 맞춘다. 0이거나 지금 열어 둔(active) 방이면 숨긴다.
  // pill은 행마다 항상 있고 room-item--unread 클래스가 표시·굵기·미리보기 진하기를 함께 켠다.
  function _paintUnread(btn: HTMLElement): void {
    const sessionId = btn.dataset.sessionId || "";
    if (!sessionId) return;
    const raw = unreadBySession.get(sessionId) || 0;
    const count = btn.classList.contains("active") ? 0 : raw;
    const pill = unreadPillByButton.get(btn);
    if (count > 0) {
      btn.classList.add("room-item--unread");
      btn.dataset.unreadCount = String(count);
      if (pill) pill.textContent = _unreadLabel(count);
    } else {
      btn.classList.remove("room-item--unread");
      btn.dataset.unreadCount = "0";
      if (pill) pill.textContent = "";
    }
  }

  function _paintAllUnread(): void {
    const list = document.getElementById("roomList");
    if (!list) return;
    list.querySelectorAll<HTMLElement>(".room-item").forEach((el) => _paintUnread(el));
  }

  function _isUnreadRecord(value: unknown): value is Record<string, number> {
    return _isRecord(value) && !Array.isArray(value)
      && Object.values(value).every((count) => typeof count === "number" && Number.isFinite(count));
  }

  // sessionId -> count 스냅샷(0 생략)으로 방별 숫자를 통째 교체하고 다시 칠한다.
  function applySessionUnreadCounts(counts: Record<string, number>): void {
    unreadBySession = new Map(Object.entries(counts).filter(([, count]) => count > 0));
    _paintAllUnread();
  }

  async function _fetchUnreadCounts(): Promise<void> {
    const seq = ++_unreadRefreshSeq;
    try {
      const res = await fetch("/api/rooms/unread-count");
      if (!res.ok) return;
      const data: unknown = await res.json();
      // 더 나중에 시작한 조회가 있으면 이 응답은 낡았다 — 버린다.
      if (seq !== _unreadRefreshSeq) return;
      if (_isRecord(data) && data.ok === true && _isUnreadRecord(data.sessionUnreadCounts)) {
        applySessionUnreadCounts(data.sessionUnreadCounts);
      }
    } catch (err) {
      // 숫자는 부가 정보 — 실패하면 직전 숫자를 유지하고 다음 신호에서 다시 맞춘다.
    }
  }

  // WS message-unread-total(새 메시지 수신·내 읽음 처리)과 재접속 시 호출된다. 그 페이로드는
  // 총계 숫자 하나뿐이라(방 정보 비노출 계약) 방별 숫자는 self-scope REST로 다시 읽는다.
  // 연속 신호는 짧게 묶어 한 번만 조회한다.
  function refreshUnreadCounts(): void {
    if (_unreadRefreshTimer !== null) clearTimeout(_unreadRefreshTimer);
    _unreadRefreshTimer = setTimeout(() => {
      _unreadRefreshTimer = null;
      void _fetchUnreadCounts();
    }, UNREAD_REFRESH_DEBOUNCE_MS);
  }

  function _commitSession(sessionId: string): CoreRoom | null {
    const list = document.getElementById("roomList");
    const room = roomBySession.get(sessionId) || null;
    if (!list) return room;
    const buttons = Array.from(list.querySelectorAll<HTMLButtonElement>(".room-item"));
    const hasCommittedRoom = typeof wsClient !== "undefined" && !!wsClient?._currentRoomId;
    const target = room
      ? buttons.find((btn) => btn.dataset.sessionId === sessionId)
      : !hasCommittedRoom ? buttons.find((btn) => btn.classList.contains("room-item-agent")) : undefined;
    if (target) _setActive(target);
    _toggleGroupInviteButton(room);
    return room;
  }

  // group-create.js의 초대 진입점(#groupInviteBtn)을 연 방의 roomType에 맞춰 노출/은닉한다.
  // dm 방도 초대 가능 — 초대 시 서버가 group으로 승격한다(1:1 고정 폐지). team 방·에이전트
  // 방에는 초대 진입을 노출하지 않는다(team은 전원 가시라 초대 개념이 없음).
  function _toggleGroupInviteButton(room: CoreRoom | null): void {
    const btn = document.getElementById("groupInviteBtn");
    if (!btn) return;
    if (room && (room.roomType === "group" || room.roomType === "dm")) {
      btn.dataset.roomId = room.id;
      btn.style.display = "";
    } else {
      btn.style.display = "none";
    }
  }

  function _avatarBackground(room: CoreRoom): string {
    if (room.roomType === "team") return "linear-gradient(135deg, var(--accent), #6e4ac4)";
    if (room.roomType === "group") return "linear-gradient(135deg, #2e9e7b, var(--accent))";
    return _slateColor(room.id || room.title);
  }

  // CAP20 일부: 최근 메시지 미리보기(라우트가 실어 보내는 lastMessagePreview). 없으면
  // 렌더하지 않는다. flex-wrap + flex-basis:100%로 title/badge 줄 아래 새 줄에 표시한다
  // (title을 별도 div로 감싸지 않아 기존 room-item 자식 구조를 그대로 유지).
  // 배치·글자색은 sidebar.css(.sidebar .room-preview)가 소유한다 — 인라인 color는 안 읽은 방의
  // 진한 미리보기 규칙을 이겨 버리므로 두지 않는다.
  function _renderPreview(room: CoreRoom): HTMLSpanElement | null {
    if (!room.lastMessagePreview) return null;
    const preview = document.createElement("span");
    preview.className = "room-preview";
    preview.textContent = room.lastMessagePreview;
    return preview;
  }

  // 오른쪽 아래(미리보기 줄 끝) 안 읽음 pill. 행마다 항상 두고 _paintUnread가 숫자·표시를 맞춘다.
  function _renderUnreadPill(): HTMLSpanElement {
    const pill = document.createElement("span");
    pill.className = "room-unread-count";
    return pill;
  }

  function _renderRoomRow(room: CoreRoom): HTMLDivElement {
    const row = document.createElement("div");
    row.className = "room-row";

    const btn = document.createElement("button");
    btn.className = "room-item";
    btn.dataset.roomId = room.id;
    btn.dataset.sessionId = room.sessionId || "";

    const avatar = document.createElement("span");
    avatar.className = "room-avatar";
    avatar.style.background = _avatarBackground(room);
    avatar.textContent = _initial(_roomTitle(room));

    const title = document.createElement("span");
    title.className = "room-title";
    title.textContent = _roomTitle(room);

    const badge = document.createElement("span");
    badge.className = `room-type-badge room-type-badge--${room.roomType || "group"}`;
    badge.textContent = _typeLabel(room.roomType);

    btn.appendChild(avatar);
    btn.appendChild(title);
    btn.appendChild(badge);

    const preview = _renderPreview(room);
    if (preview) {
      btn.classList.add("room-item--has-preview");
      btn.appendChild(preview);
    }
    const pill = _renderUnreadPill();
    unreadPillByButton.set(btn, pill);
    btn.appendChild(pill);
    _paintUnread(btn);

    btn.addEventListener("click", () => {
      if (!room.sessionId) return;
      // D14: 다른 룸으로 이동하면 열려 있던 에이전트 대화 선택 픽커를 닫는다.
      if (typeof window !== "undefined" && window.AgentConversationPicker) window.AgentConversationPicker.hide();
      if (wsClient && wsClient.isConnected) {
        if (!wsClient.pendingResumeSessionId && typeof currentSessionId !== "undefined"
            && currentSessionId === room.sessionId && wsClient._currentSessionId === room.sessionId
            && wsClient._currentRoomId === room.id) {
          _commitSession(room.sessionId);
          if (typeof bindRoomHeader === "function") bindRoomHeader(room.id, { preserveOnError: true });
          router.navigate("chat");
          return;
        }
        wsClient.resumeSession(room.sessionId, { roomId: room.id });
      } else if (typeof showSummaryToast === "function") {
        showSummaryToast("서버에 연결된 뒤 방을 열 수 있어요.", true);
      }
    });

    _attachRoomContextMenu(btn, room);

    row.appendChild(btn);
    return row;
  }

  // ===== 방 컨텍스트 메뉴 (우클릭 / 모바일 long-press) =====
  // 기존 진입점만 재사용한다: 이름 변경(GroupCreate.openRename → PATCH /api/rooms/:id),
  // 멤버 초대(GroupCreate.openInvite → POST /:id/members, dm/group),
  // 나가기(DELETE /api/rooms/:id/members/me). team 방은 전원 가시·부트스트랩 관리라
  // 초대/나가기를 노출하지 않는다.

  let _roomContextMenuListenersAttached = false;

  function _closeRoomContextMenu() {
    if (_roomContextMenuListenersAttached) {
      document.removeEventListener("click", _closeRoomContextMenu);
      document.removeEventListener("contextmenu", _closeRoomContextMenu);
      _roomContextMenuListenersAttached = false;
    }
    const menu = document.getElementById("roomContextMenu");
    if (menu) menu.remove();
  }

  function _openRoomContextMenu(room: CoreRoom, x: number, y: number): void {
    _closeRoomContextMenu();
    const menu = document.createElement("div");
    menu.id = "roomContextMenu";
    menu.className = "room-context-menu";
    menu.setAttribute("role", "menu");

    const addItem = (label: string, onSelect: () => void, danger = false): void => {
      const item = document.createElement("button");
      item.type = "button";
      item.className = "room-context-menu-item" + (danger ? " room-context-menu-item-danger" : "");
      item.setAttribute("role", "menuitem");
      item.textContent = label;
      item.addEventListener("click", () => {
        _closeRoomContextMenu();
        onSelect();
      });
      menu.appendChild(item);
    };

    addItem("이름 변경", () => {
      if (typeof GroupCreate !== "undefined") GroupCreate.openRename(room.id, room.title || "");
    });
    if (room.roomType === "dm" || room.roomType === "group") {
      addItem("멤버 초대", () => {
        if (typeof GroupCreate !== "undefined") GroupCreate.openInvite(room.id);
      });
      addItem("나가기", () => { _leaveRoom(room); }, true);
    }

    document.body.appendChild(menu);
    // 화면 밖으로 나가지 않게 배치.
    const rect = menu.getBoundingClientRect();
    menu.style.left = `${Math.max(8, Math.min(x, window.innerWidth - rect.width - 8))}px`;
    menu.style.top = `${Math.max(8, Math.min(y, window.innerHeight - rect.height - 8))}px`;

    setTimeout(() => {
      document.addEventListener("click", _closeRoomContextMenu, { once: true });
      document.addEventListener("contextmenu", _closeRoomContextMenu, { once: true });
      _roomContextMenuListenersAttached = true;
    }, 0);
  }

  async function _leaveRoom(room: CoreRoom): Promise<void> {
    if (!confirm(`'${_roomTitle(room)}' 방에서 나갈까요?`)) return;
    try {
      const res = await fetch(`/api/rooms/${room.id}/members/me`, { method: "DELETE" });
      const data: unknown = await res.json().catch(() => ({ ok: false }));
      const ok = _isRecord(data) && data.ok === true;
      const error = _isRecord(data) && typeof data.error === "string" ? data.error : "leave_failed";
      if (!res.ok || !ok) throw new Error(error);
    } catch (err) {
      console.error("[room-list] 나가기 실패:", err);
      alert("나가기에 실패했어요. 다시 시도해 주세요.");
      return;
    }
    refresh();
    // 나간 방을 보고 있었으면 에이전트(비룸) 세션으로 전환한다.
    if (room.sessionId && typeof currentSessionId !== "undefined" && currentSessionId === room.sessionId
        && wsClient && wsClient.isConnected) {
      const latestAgentSessionId = _latestNonRoomSessionId();
      if (latestAgentSessionId) wsClient.resumeSession(latestAgentSessionId);
      else wsClient.newSession();
      // Phase 10: 309행 패턴 미러 — 비룸 전환이므로 currentRoomId SSOT를 비운다.
      wsClient.setCurrentRoomId(null);
      _toggleGroupInviteButton(null);
    }
  }

  function _attachRoomContextMenu(btn: HTMLButtonElement, room: CoreRoom): void {
    btn.addEventListener("contextmenu", (e) => {
      e.preventDefault();
      _openRoomContextMenu(room, e.clientX, e.clientY);
    });
    // 모바일 long-press(500ms) — 스크롤/이동/손 떼기 시 취소.
    let pressTimer: number | null = null;
    btn.addEventListener("touchstart", (e) => {
      const touch = e.touches[0];
      if (!touch) return;
      pressTimer = setTimeout(() => {
        pressTimer = null;
        _openRoomContextMenu(room, touch.clientX, touch.clientY);
      }, 500);
    }, { passive: true });
    const cancelPress = () => {
      if (pressTimer) {
        clearTimeout(pressTimer);
        pressTimer = null;
      }
    };
    btn.addEventListener("touchend", cancelPress);
    btn.addEventListener("touchmove", cancelPress);
    btn.addEventListener("touchcancel", cancelPress);
  }

  // 최신 비룸 세션 id — onSessionInfo가 갱신하는 latestAgentSessions(app.js 전역, D07 필터
  // 적용)를 소비한다. 사이드바에 '이전 대화' DOM이 없으므로 DOM이 아닌 데이터가 단일 소스.
  // 필터가 우회된 경우를 대비해 roomSessionIds로 한 번 더 거른다.
  function _latestNonRoomSessionId() {
    const sessions = typeof latestAgentSessions !== "undefined" ? latestAgentSessions : [];
    const actorId = typeof window !== "undefined"
      ? window.BigtionAuth?.actorContext?.actor?.actor || null
      : null;
    for (const s of sessions) {
      if (!s || !s.id || roomSessionIds.has(s.id)) continue;
      // 서버가 actor 필터링한 목록만 소비한다. actor context가 이미 로드된 경우에는
      // ownerActorId도 한 번 더 대조해 오래된/오염된 전역 캐시를 열지 않는다.
      const ownerActorId = "ownerActorId" in s && typeof s.ownerActorId === "string" ? s.ownerActorId : null;
      if (actorId && ownerActorId && ownerActorId !== actorId) continue;
      return s.id;
    }
    return null;
  }

  // 에이전트 방(나와의 채팅) — DB 룸이 아닌 1급 고정 엔트리.
  // 클릭 시 기존 그룹챗 세션 경로(app.js currentSessionId + ws-client)를 재사용한다.
  function _renderAgentRoomRow() {
    const row = document.createElement("div");
    row.className = "room-row";

    const btn = document.createElement("button");
    btn.className = "room-item room-item-agent";

    const avatar = document.createElement("span");
    avatar.className = "room-avatar room-avatar-agent";
    avatar.textContent = "AI";

    const title = document.createElement("span");
    title.className = "room-title";
    title.textContent = "에이전트 방";

    const badge = document.createElement("span");
    badge.className = "room-type-badge room-type-badge-agent";
    badge.textContent = "AI";

    btn.appendChild(avatar);
    btn.appendChild(title);
    btn.appendChild(badge);

    btn.addEventListener("click", () => {
      const previousActive = document.querySelector<HTMLButtonElement>("#roomList .room-item.active");
      const header = document.getElementById("chatHeader");
      const chips = document.getElementById("chatMemberChips");
      const invite = document.getElementById("groupInviteBtn");
      const previousHeaderText = header?.textContent || "";
      const previousChipsHtml = chips?.innerHTML || "";
      const previousInviteDisplay = invite?.style.display || "";
      const previousInviteRoomId = invite?.dataset.roomId;

      _setActive(btn);
      _toggleGroupInviteButton(null);
      if (typeof closeDrawer === "function") closeDrawer();
      if (wsClient && typeof wsClient.cancelPendingSessionTransition === "function") {
        wsClient.cancelPendingSessionTransition();
      }
      // 선택기는 아직 세션 전환이 아니다. 실제 room/session은 서버 ack까지 유지한다.
      // D14: 자동 resume/new 분기를 제거하고 대화 선택 픽커를 띄운다(intake-notes:280).
      // 클릭 경로의 ownerActorId 2차 대조 심층방어(_latestNonRoomSessionId)도 이 분기와
      // 함께 사라지지만, 서버 canAccessSession이 세션 접근 권위를 계속 유지한다.
      // agent-conversation-picker.ts는 window.AgentConversationPicker로만 노출된다(323행 BigtionAuth와 동일 접근 패턴).
      if (typeof window !== "undefined" && window.AgentConversationPicker) {
        window.AgentConversationPicker.show(() => {
          if (previousActive?.isConnected) _setActive(previousActive);
          else if (typeof currentSessionId !== "undefined" && currentSessionId) _commitSession(currentSessionId);
          if (header) header.textContent = previousHeaderText;
          if (chips) chips.innerHTML = previousChipsHtml;
          if (invite) {
            invite.style.display = previousInviteDisplay;
            if (previousInviteRoomId === undefined) delete invite.dataset.roomId;
            else invite.dataset.roomId = previousInviteRoomId;
          }
        });
      }
      router.navigate("chat");
    });

    row.appendChild(btn);
    return row;
  }

  function _updateCount(n: number): void {
    const el = document.getElementById("roomCount");
    if (el) el.textContent = n ? `· ${n}` : "";
  }

  function _render(rooms: CoreRoom[]): void {
    const list = document.getElementById("roomList");
    if (!list) return;
    const primaryDefaultRoom = rooms.find((room) => room.isDefault === true) || null;
    const orderedRooms = primaryDefaultRoom
      ? [primaryDefaultRoom, ...rooms.filter((room) => room !== primaryDefaultRoom)]
      : rooms;

    roomSessionIds = new Set(orderedRooms.flatMap((room) => room.sessionId ? [room.sessionId] : []));
    roomIdBySession = new Map(orderedRooms.flatMap((room): Array<[string, string]> => room.sessionId ? [[room.sessionId, room.id]] : []));
    roomBySession = new Map(orderedRooms.flatMap((room): Array<[string, CoreRoom]> => room.sessionId ? [[room.sessionId, room]] : []));
    roomById = new Map(orderedRooms.map((room) => [room.id, room]));
    defaultRoom = primaryDefaultRoom;
    // 목록 응답이 방별 숫자를 실어 왔으면(읽음 기록 가용) 그 값이 최신 스냅샷이다.
    if (orderedRooms.some((room) => typeof room.unreadCount === "number")) {
      unreadBySession = new Map(orderedRooms.flatMap((room): Array<[string, number]> =>
        room.sessionId && typeof room.unreadCount === "number" && room.unreadCount > 0
          ? [[room.sessionId, room.unreadCount]]
          : []));
    }

    // 에이전트 방(고정 1급 엔트리) 1개 + DB 룸 개수.
    _updateCount(orderedRooms.length + 1);

    list.innerHTML = "";
    if (primaryDefaultRoom) list.appendChild(_renderRoomRow(primaryDefaultRoom));
    list.appendChild(_renderAgentRoomRow());

    if (orderedRooms.length === 0) {
      const empty = document.createElement("div");
      empty.className = "session-empty";
      empty.textContent = "참여 중인 대화방이 없어요";
      list.appendChild(empty);
    }

    orderedRooms.forEach((room) => {
      if (room === primaryDefaultRoom) return;
      list.appendChild(_renderRoomRow(room));
    });
    // 목록 fetch와 WS commit은 독립적으로 완료된다. 목록 재렌더도 확정된 선택을 투영해야 한다.
    if (typeof window !== "undefined" && window.AgentConversationPicker?.isVisible()) {
      const agentButton = list.querySelector<HTMLButtonElement>(".room-item-agent");
      if (agentButton) _setActive(agentButton);
      _toggleGroupInviteButton(null);
    } else if (typeof currentSessionId !== "undefined" && currentSessionId) {
      _commitSession(currentSessionId);
    }
  }

  async function refresh(): Promise<CoreRoom[]> {
    try {
      const res = await fetch("/api/rooms");
      if (!res.ok) {
        _loaded = false;
        _render([]);
        return [];
      }
      const data: unknown = await res.json();
      const rooms = _isRecord(data) && data.ok === true && Array.isArray(data.rooms)
        ? data.rooms.filter(_isRoom)
        : [];
      await _buildMemberLabels(rooms);
      _render(rooms);
      _loaded = true;
      return rooms;
    } catch (err) {
      console.error("[room-list] fetch failed:", err);
      _loaded = false;
      _render([]);
      return [];
    }
  }

  function getSessionIds(): Set<string> {
    return roomSessionIds;
  }

  function getRoomIdForSession(sessionId: string): string | null {
    return roomIdBySession.get(sessionId) ?? null;
  }

  function getDefaultRoom(): CoreRoom | null {
    return defaultRoom && defaultRoom.sessionId ? defaultRoom : null;
  }

  function getRoomBySession(sessionId: string): CoreRoom | null {
    return roomBySession.get(sessionId) ?? null;
  }

  function getRoomById(roomId: string): CoreRoom | null {
    return roomById.get(roomId) ?? null;
  }

  function resolveRoomNavigationTarget(params: Record<string, unknown> = {}): RoomNavigationTarget {
    const roomId = typeof params.roomId === "string" && params.roomId.trim() ? params.roomId : null;
    const sessionId = typeof params.sessionId === "string" && params.sessionId.trim() ? params.sessionId : null;
    if (!roomId && !sessionId) return { status: "none" };

    const byRoom = roomId ? getRoomById(roomId) : null;
    const bySession = sessionId ? getRoomBySession(sessionId) : null;

    if (roomId && sessionId) {
      if (!byRoom || !bySession || byRoom.id !== bySession.id || byRoom.sessionId !== sessionId) {
        return { status: "invalid", reason: "denied" };
      }
      return byRoom.sessionId ? { status: "room", room: byRoom } : { status: "invalid", reason: "missing_session" };
    }

    const room = byRoom || bySession;
    // 방 목록에 없는 session-only 링크는 비룸 대화일 수 있다.
    // 접근 허용 여부는 resume-session 서버 경계가 판단한다.
    if (!roomId && sessionId && !room) return { status: "session", sessionId };
    if (!room) return { status: "invalid", reason: "denied" };
    return room.sessionId ? { status: "room", room } : { status: "invalid", reason: "missing_session" };
  }

  function isLoaded(): boolean {
    return _loaded;
  }

  function init(): Promise<CoreRoom[]> {
    return refresh();
  }

  return {
    init,
    refresh,
    getSessionIds,
    getRoomIdForSession,
    getDefaultRoom,
    getRoomBySession,
    getRoomById,
    resolveRoomNavigationTarget,
    isLoaded,
    commitSession: _commitSession,
    refreshUnreadCounts,
    applySessionUnreadCounts,
  };
})();
